/**
 * Shared money formatter — `window.YarisMoney.format(cents)`
 * ----------------------------------------------------------
 * Currency-agnostic: the same file ships to TR, UK, US and EU.
 *
 * The currency symbol and its position come from the shop's own
 * `shop.money_format`, so each market keeps what the merchant configured
 * (`1.885 TL`, `£1,885`, `€1.885`). Only the amount placeholder is rewritten,
 * because a shop's raw format hardcodes one separator style and that style is
 * wrong as soon as the storefront is viewed in another language. The grouping
 * and decimal characters are therefore taken from the active storefront locale
 * and mapped onto the matching Shopify placeholder.
 *
 * Round amounts render without trailing zeroes (`1.885 TL`, not `1.885,00 TL`),
 * matching what the size sheet and the frame configurator already shipped.
 *
 * It lives in this file because `size-option-sheet.js` is the one asset among
 * the callers (size sheet, frame configurator, frame upsell modal, add-to-cart
 * total) that both layouts load on every page.
 */
(function () {
  if (window.YarisMoney) return;

  const PLACEHOLDER = /\{\{\s*\w+\s*\}\}/;

  function locale() {
    return document.documentElement.getAttribute('lang') || 'en';
  }

  function isoCode() {
    return (typeof theme !== 'undefined' && theme.shopSettings?.isoCode) || '';
  }

  /**
   * Which Shopify amount placeholder family matches this storefront's locale.
   * Apostrophe grouping (de-CH) has no `no_decimals` counterpart in Shopify.
   */
  function separatorSuffix() {
    let group = ',';
    try {
      // Seven digits: some locales (pl) only start grouping above 10 000, so a
      // smaller probe would report no separator at all.
      const parts = new Intl.NumberFormat(locale()).formatToParts(1234567.5);
      group = parts.find((part) => part.type === 'group')?.value ?? ',';
    } catch (e) {
      /* keep the comma default */
    }

    if (group === '.') return { suffix: '_with_comma_separator', noDecimals: true };
    if (group === "'" || group === '\u2019') return { suffix: '_with_apostrophe_separator', noDecimals: false };
    if (/\s|\u00a0|\u202f/.test(group)) return { suffix: '_with_space_separator', noDecimals: true };

    return { suffix: '', noDecimals: true };
  }

  function amountTemplate(cents) {
    const { suffix, noDecimals } = separatorSuffix();
    const round = Number(cents) % 100 === 0;

    return round && noDecimals ? `{{amount_no_decimals${suffix}}}` : `{{amount${suffix}}}`;
  }

  /** Used when global.js has not defined `theme.Currency`. */
  function plain(cents) {
    const amount = Number(cents) / 100;
    if (!Number.isFinite(amount)) return '';

    const digits = amount % 1 === 0 ? 0 : 2;
    const code = isoCode();

    try {
      return new Intl.NumberFormat(locale(), {
        style: code ? 'currency' : 'decimal',
        currency: code || undefined,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(digits);
    }
  }

  window.YarisMoney = {
    format(cents) {
      const value = Number(cents);
      if (!Number.isFinite(value)) return '';

      const currency = typeof theme !== 'undefined' ? theme.Currency : null;
      if (!currency || typeof currency.formatMoney !== 'function') return plain(value);

      const baseFormat = (typeof theme !== 'undefined' && theme.shopSettings?.moneyFormat) || '';
      const template = PLACEHOLDER.test(baseFormat)
        ? baseFormat.replace(PLACEHOLDER, amountTemplate(value))
        : '';

      try {
        return currency.formatMoney(value, template || baseFormat || undefined);
      } catch (e) {
        try {
          return currency.formatMoney(value, baseFormat || undefined);
        } catch (e2) {
          return plain(value);
        }
      }
    },
  };
})();

/**
 * Mobile bottom sheet for dimension dropdown variant selects.
 */
if (!customElements.get('size-option-sheet')) {
  class SizeOptionSheet extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this._selectedValue = '';
      this.boundMqlChange = this.onMqlChange.bind(this);
      this.boundSelectChange = this.onSelectChange.bind(this);
      this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.boundOptionActivate = this.onOptionActivate.bind(this);
      this.boundOutsideClick = this.onOutsideClick.bind(this);
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.trigger = this.querySelector('.size-option-sheet__trigger');
      this.triggerValue = this.querySelector('.size-option-sheet__trigger-value');
      this.triggerPrice = this.querySelector('[data-size-trigger-price]');
      this.panel = this.querySelector('.size-option-sheet__panel');
      this.overlay = this.querySelector('.size-option-sheet__overlay');
      this.list = this.querySelector('.size-option-sheet__list');
      this.closeBtn = this.querySelector('.size-option-sheet__close');
      this.optionPosition = parseInt(this.dataset.optionPosition, 10);

      const variantsJson = this.querySelector('[data-size-sheet-variants]');
      this.variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];

      this.trigger?.addEventListener('click', () => this.toggle());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.list?.addEventListener('click', this.boundOptionActivate);
      this.list?.addEventListener('pointerup', this.boundOptionActivate);
      this.select?.addEventListener('change', this.boundSelectChange);

      this.mql.addEventListener('change', this.boundMqlChange);
      this.updateMode();
      this.hasUserSelected = false;
      this.ensurePlaceholderSelected(false);

      if (new URLSearchParams(window.location.search).get('variant')) {
        this.restoreFromFormVariantId(true);
      }

      this.syncFromSelect();
      this.updateListPrices();

      if (typeof subscribe === 'function') {
        this.optionChangeUnsub = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
          this.updateListPrices();
          this.syncFromSelect();
        });

        this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || String(data?.sectionId) !== String(productInfo.dataset.section)) return;

          requestAnimationFrame(() => {
            if (this.hasUserSelected) {
              this.restoreFromFormVariantId();
            }
            this.syncFromSelect();
            this.updateListPrices();
          });
        });
      }
    }

    disconnectedCallback() {
      this.mql.removeEventListener('change', this.boundMqlChange);
      this.select?.removeEventListener('change', this.boundSelectChange);
      this.list?.removeEventListener('click', this.boundOptionActivate);
      this.list?.removeEventListener('pointerup', this.boundOptionActivate);
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.removeEventListener('click', this.boundOutsideClick, true);
      document.body.classList.remove('size-option-sheet--open');
      this.optionChangeUnsub?.();
      this.variantChangeUnsub?.();
    }

    onMqlChange() {
      // Reset any open state when crossing the breakpoint so the desktop
      // dropdown and the mobile bottom sheet never linger in a mixed state.
      this.close(false);
      this.updateMode();
    }

    updateMode() {
      const isMobile = !this.mql.matches;
      this.classList.toggle('size-option-sheet--mobile', isMobile);
      this.#syncPlaceholderDisabledState(isMobile);
    }

    #syncPlaceholderDisabledState(isMobile) {
      const placeholder = this.select?.querySelector('option[data-placeholder]');
      if (!placeholder) return;

      // iOS Safari often ignores select.value changes while a disabled option stays selected.
      if (isMobile) {
        placeholder.removeAttribute('disabled');
      } else {
        placeholder.setAttribute('disabled', 'disabled');
      }
    }

    getVariantOptionValue(variant, position) {
      if (!variant) return null;
      if (Array.isArray(variant.options) && variant.options[position - 1] != null) {
        return variant.options[position - 1];
      }
      return variant[`option${position}`] || null;
    }

    getOtherSelectedOptions() {
      const variantSelects = this.closest('variant-selects');
      if (!variantSelects) return {};

      const selections = {};
      variantSelects.querySelectorAll('size-option-sheet').forEach((sheet) => {
        const pos = parseInt(sheet.dataset.optionPosition, 10);
        if (pos === this.optionPosition) return;
        const val = sheet._selectedValue || sheet.select?.value;
        if (val) selections[pos] = val;
      });

      variantSelects.querySelectorAll('select:not(.size-option-sheet__select)').forEach((select) => {
        const name = select.getAttribute('name') || '';
        const match = name.match(/options\[(.+)\]/);
        if (!match || !select.value) return;
        const pos = this.getSelectPosition(variantSelects, select);
        if (pos && pos !== this.optionPosition) selections[pos] = select.value;
      });

      variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
        const fieldset = input.closest('fieldset');
        if (!fieldset) return;
        const pos = this.getFieldsetPosition(variantSelects, fieldset);
        if (pos && pos !== this.optionPosition) selections[pos] = input.value;
      });

      return selections;
    }

    getSelectPosition(variantSelects, select) {
      const sheet = select.closest('size-option-sheet');
      if (sheet) return parseInt(sheet.dataset.optionPosition, 10);
      const selects = [...variantSelects.querySelectorAll('select')];
      return selects.indexOf(select) + 1;
    }

    getFieldsetPosition(variantSelects, fieldset) {
      const fieldsets = [...variantSelects.querySelectorAll('fieldset')];
      return fieldsets.indexOf(fieldset) + 1;
    }

    findVariantForValue(value) {
      const others = this.getOtherSelectedOptions();

      const exact = this.variants.find((variant) => {
        if (this.getVariantOptionValue(variant, this.optionPosition) !== value) return false;
        for (const [pos, val] of Object.entries(others)) {
          if (this.getVariantOptionValue(variant, parseInt(pos, 10)) !== val) return false;
        }
        return true;
      });

      if (exact) return exact;

      return this.variants.find(
        (variant) => this.getVariantOptionValue(variant, this.optionPosition) === value
      );
    }

    allDimensionSheetsSelected() {
      const variantSelects = this.closest('variant-selects');
      if (!variantSelects) return true;

      const sheets = variantSelects.querySelectorAll('size-option-sheet');
      if (!sheets.length) return true;

      return [...sheets].every((sheet) => !sheet.isPlaceholderSelected());
    }

    getSelectedValue() {
      if (this.isPlaceholderSelected()) return null;
      return this._selectedValue || this.select?.value || null;
    }

    syncVariantIdToForm() {
      if (this.isPlaceholderSelected()) return;
      if (!this.allDimensionSheetsSelected()) return;

      const value = this._selectedValue || this.select?.value;
      if (!value) return;

      const variant = this.findVariantForValue(value);
      if (!variant?.id) return;

      const productForm = this.closest('product-info')?.querySelector('product-form');
      const input = productForm?.querySelector('input[name="id"]');
      if (!input) return;

      input.value = String(variant.id);
      input.disabled = !variant.available;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    restoreFromFormVariantId(force = false) {
      if (!force && !this.hasUserSelected) return;

      const productForm = this.closest('product-info')?.querySelector('product-form');
      const variantId = productForm?.querySelector('input[name="id"]')?.value;
      if (!variantId || !this.variants.length || !this.select) return;

      const variant = this.variants.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return;

      const optionValue = this.getVariantOptionValue(variant, this.optionPosition);
      if (!optionValue) return;

      const matchingOption = Array.from(this.select.options).find(
        (option) => option.value === optionValue && option.dataset.placeholder !== 'true'
      );
      if (!matchingOption) return;

      this.hasUserSelected = true;
      this.selectOptionByValue(optionValue);
    }

    formatMoney(cents) {
      return window.YarisMoney.format(cents);
    }

    priceHtml(priceCents, compareCents) {
      const price = Number(priceCents);
      const compare = Number(compareCents);
      let html = '';
      if (Number.isFinite(compare) && compare > price) {
        html += `<s class="size-option-sheet__option-compare"><price-money><bdi>${this.formatMoney(compare)}</bdi></price-money></s>`;
      }
      if (Number.isFinite(price)) {
        html += `<price-money><bdi>${this.formatMoney(price)}</bdi></price-money>`;
      }
      return html;
    }

    ensurePlaceholderSelected(dispatchChange = false) {
      const placeholderOption = this.select?.querySelector('option[data-placeholder]');
      if (!placeholderOption || this.hasUserSelected) return;

      this._selectedValue = '';
      Array.from(this.select.options).forEach((option) => {
        option.selected = false;
        option.removeAttribute('selected');
      });
      this.select.selectedIndex = 0;
      placeholderOption.selected = true;
      placeholderOption.setAttribute('selected', 'selected');

      if (dispatchChange) {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    isPlaceholderSelected() {
      if (this.hasUserSelected && this._selectedValue) return false;
      if (!this.select) return true;

      const selectedOption = this.getSelectedOption();
      return !selectedOption || selectedOption.dataset.placeholder === 'true';
    }

    getPlaceholderLabel() {
      return this.dataset.placeholderLabel || 'Boyut Seçiniz';
    }

    updateSubmitState() {
      const productForm = this.closest('product-info')?.querySelector('product-form');

      if (!this.isPlaceholderSelected()) {
        if (typeof productForm?.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }
      }

      if (typeof productForm?.applySubmitButtonState === 'function') {
        productForm.applySubmitButtonState();
      }
    }

    updateListPrices() {
      this.list?.querySelectorAll('.size-option-sheet__option').forEach((btn) => {
        const value = btn.getAttribute('data-value');
        const variant = this.findVariantForValue(value);
        let priceEl = btn.querySelector('.size-option-sheet__option-price');

        if (variant) {
          btn.dataset.variantPrice = String(variant.price);
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            btn.dataset.variantComparePrice = String(variant.compare_at_price);
          } else {
            delete btn.dataset.variantComparePrice;
          }
          if (!priceEl) {
            priceEl = document.createElement('span');
            priceEl.className = 'size-option-sheet__option-price';
            btn.appendChild(priceEl);
          }
          priceEl.innerHTML = this.priceHtml(variant.price, variant.compare_at_price);
          btn.disabled = !variant.available;
          btn.closest('.size-option-sheet__item')?.classList.toggle('size-option-sheet__item--disabled', !variant.available);
        }
      });
      this.updateTriggerPrice();
    }

    updateTriggerPrice() {
      if (!this.triggerPrice) return;
      if (this.isPlaceholderSelected()) {
        this.triggerPrice.hidden = true;
        this.triggerPrice.innerHTML = '';
        return;
      }

      const value = this._selectedValue || this.getSelectedOption()?.value;
      const variant = this.findVariantForValue(value);
      if (!variant) {
        this.triggerPrice.hidden = true;
        this.triggerPrice.innerHTML = '';
        return;
      }

      this.triggerPrice.hidden = false;
      this.triggerPrice.innerHTML = this.priceHtml(variant.price, variant.compare_at_price);
    }

    onSelectChange() {
      this.syncSelectedValueFromNative();
      this.syncFromSelect();
    }

    syncSelectedValueFromNative() {
      if (!this.select) return;

      const selectedOption = this.select.options[this.select.selectedIndex];

      if (!selectedOption || selectedOption.dataset.placeholder === 'true') {
        this._selectedValue = '';
        return;
      }

      this._selectedValue = selectedOption.value;
      this.hasUserSelected = true;
    }

    syncFromSelect() {
      if (!this.select || !this.triggerValue) return;

      const isPlaceholder = this.isPlaceholderSelected();
      const selectedOption = isPlaceholder ? null : this.getSelectedOption();
      const value = isPlaceholder ? '' : (this._selectedValue || selectedOption?.value || '');
      const label = isPlaceholder
        ? this.getPlaceholderLabel()
        : (selectedOption?.textContent?.trim() || value);

      this.triggerValue.setAttribute('data-size-value', value);
      this.triggerValue.textContent = label;
      this.triggerValue.classList.toggle('size-option-sheet__trigger-value--placeholder', isPlaceholder);
      this.updateTriggerPrice();

      this.list?.querySelectorAll('.size-option-sheet__option').forEach((btn) => {
        const isSelected = !isPlaceholder && btn.getAttribute('data-value') === value;
        btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        btn.closest('.size-option-sheet__item')?.classList.toggle('size-option-sheet__item--selected', isSelected);
      });

      this.updateSubmitState();
      if (!isPlaceholder) {
        this.hasUserSelected = true;
        this.syncVariantIdToForm();
      }

      window.YarisSizeUnits?.applyAll?.();
    }

    toggle() {
      if (this.classList.contains('is-open')) this.close();
      else this.open();
    }

    open() {
      if (!this.panel) return;
      this.updateListPrices();

      // Desktop: absolute dropdown that overlays the content below the trigger.
      if (this.mql.matches) {
        this.panel.hidden = false;
        this.classList.add('is-open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.boundOutsideClick, true);
        document.addEventListener('keydown', this.boundDocumentKeydown);
        window.YarisSizeUnits?.applyAll?.();
        return;
      }

      // Mobile: slide-up bottom sheet.
      this.panel.hidden = false;
      if (this.overlay) this.overlay.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });

      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('size-option-sheet--open');
      document.addEventListener('keydown', this.boundDocumentKeydown);
      window.YarisSizeUnits?.applyAll?.();
    }

    close(animate = true) {
      if (!this.panel) return;

      const isDesktop = this.mql.matches;
      this.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.boundDocumentKeydown);
      document.removeEventListener('click', this.boundOutsideClick, true);

      if (isDesktop) {
        this.panel.hidden = true;
        return;
      }

      document.body.classList.remove('size-option-sheet--open');
      const hide = () => {
        this.panel.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
      };

      if (animate) {
        setTimeout(hide, 350);
      } else {
        hide();
      }
    }

    onOutsideClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    getSelectedOption() {
      if (!this.select) return null;

      const nativeSelected = this.select.options[this.select.selectedIndex];
      if (
        nativeSelected &&
        nativeSelected.dataset.placeholder !== 'true' &&
        this._selectedValue &&
        this._selectedValue !== nativeSelected.value
      ) {
        this._selectedValue = nativeSelected.value;
      }

      if (this._selectedValue) {
        const matched = Array.from(this.select.options).find(
          (option) => option.value === this._selectedValue && option.dataset.placeholder !== 'true'
        );
        if (matched) return matched;
      }

      const selectedOption = this.select.options[this.select.selectedIndex];
      if (selectedOption?.dataset.placeholder === 'true') return null;

      return selectedOption || null;
    }

    getSelectedOptionValueId() {
      return this.getSelectedOption()?.dataset.optionValueId || null;
    }

    findOptionByValue(value) {
      if (!value || !this.select) return null;

      return Array.from(this.select.options).find(
        (option) => option.value === value && option.dataset.placeholder !== 'true'
      );
    }

    selectOptionByValue(value) {
      const matchingOption = this.findOptionByValue(value);
      if (!matchingOption || !this.select) return false;

      this._selectedValue = value;
      this.hasUserSelected = true;

      Array.from(this.select.options).forEach((option) => {
        option.selected = false;
        option.removeAttribute('selected');
      });

      matchingOption.selected = true;
      matchingOption.setAttribute('selected', 'selected');
      this.select.selectedIndex = matchingOption.index;

      // Setting .value after selectedIndex helps WebKit keep the native select in sync.
      try {
        this.select.value = value;
      } catch (error) {
        // Ignore — selectedIndex is authoritative for the sheet UI.
      }

      if (this.select.value !== value) {
        this.select.selectedIndex = matchingOption.index;
      }

      return true;
    }

    onOptionActivate(event) {
      if (event.type === 'pointerup') {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this._handledByPointer = true;
        window.setTimeout(() => {
          this._handledByPointer = false;
        }, 500);
      } else if (this._handledByPointer) {
        return;
      }

      const btn = event.target.closest('.size-option-sheet__option');
      if (!btn || btn.disabled) return;

      const value = btn.getAttribute('data-value');
      if (!value || !this.select) return;
      if (!this.selectOptionByValue(value)) return;

      event.preventDefault();
      this.syncFromSelect();
      this.close(false);

      requestAnimationFrame(() => {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  customElements.define('size-option-sheet', SizeOptionSheet);
}
